import {
  FlatList,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import React, { memo, useMemo, useState } from "react";
import { GET_JOB_CARD_DATA } from "../../query";
import { useQuery } from "@apollo/client";
import { clientExternalV2, clientV2 } from "../../../../common";
import { FONT_FAMILY, THEME_COLORS } from "../../../../common/themes";
import moment from "moment";
import BeforeAfterImages from "../../../../common/component/BeforeAfterImages";
import MjrTimelineComponent from "../../../../common/component/MjrTimelineComponent";
import { Entypo as Icon } from "@expo/vector-icons";
import PartDetailsToolTip from "../../../../common/component/PartDetailsToolTip";
import JobCardSparePartToolTip from "../../../../common/component/JobCardSparePartToolTip";

interface IJobCardViewProps {
  jobCardId: any;
}
const header = [
  "Car Part",
  "Issues",
  "Fixes",
  "Spare Part",
  "Quantity",
  "Part Cost",
  "Labour Cost",
  "Total Cost",
  "Status",
];

const RowComponent = memo(({ row, isSparePartsServiceAvailable }: any) => {
  const [showTimeline, setShowTimeline] = useState(false);

  const [
    status,
    statusColor,
    partDetails,
    timelineData,
    beforeImages,
    afterImages,
  ] = useMemo(() => {
    let status = "",
      statusColor = "";

    if (row?.verification == "VERIFIED") {
      status = "Verified";
      statusColor = "#21CC9E";
    } else if (row?.verification == "REJECTED") {
      status = "Rejected";
      statusColor = "#FF9A3D";
    } else if (row?.isVerifiable) {
      status = "Verification Pending";
      statusColor = "grey";
    } else
      switch (row?.approvalStatus) {
        case "APPROVED":
          status = "Approved";
          statusColor = "#21CC9E";
          break;
        case "DISCARDED":
          status = "Discarded";
          statusColor = "#FF9A3D";
          break;
        case "REJECTED":
          status = "Rejected";
          statusColor = "#FF9A3D";
          break;
        default:
          status = "Approval Pending";
          statusColor = "grey";
      }

    const partDetails = row?.partDetails?.map((item) => JSON.parse(item));

    const timelineData = row?.timeline?.length
      ? row.timeline.reduce((acc, item, index) => {
          if (index % 2) return acc;
          if (row?.timeline[index + 1])
            acc.push({
              ...item,
              date: moment(row?.timeline[index + 1].label).format(
                "Do MMM, h:mm a"
              ),
            });
          else acc.push(item);
          return acc;
        }, [])
      : [];

    let beforeImages = [],
      afterImages = [];
    if (row?.images?.length) {
      row.images.map((image: any) => {
        if (image.label == "BEFORE") beforeImages.push(image);
        else if (image.label == "AFTER") afterImages.push(image);
      });
    }
    return [
      status,
      statusColor,
      partDetails,
      timelineData,
      beforeImages,
      afterImages,
    ];
  }, [row]);

  const totalCost =
    (row?.partCount || 0) * (row?.partCost || 0) + (row?.labourCost || 0);

  return (
    <View
      style={{ borderBottomWidth: 1, borderBottomColor: "rgb(230,230,230)" }}
    >
      <View
        style={{
          flexDirection: "row",
          backgroundColor: "#FAFAFA",
          paddingVertical: 15,
          paddingHorizontal: 5,
        }}
      >
        <View style={{ flex: 1, paddingHorizontal: 6 }}>
          <Text style={styles.rowText}>{row?.part?.name}</Text>
          <BeforeAfterImages
            beforeImages={beforeImages || []}
            afterImages={afterImages || []}
          />
        </View>
        <View style={{ flex: 1, paddingHorizontal: 6 }}>
          {row?.jobRow
            ? row.jobRow?.subJobRow?.map((sjr, i) => (
                <Text key={i} style={styles.rowText}>
                  {sjr?.issue?.name}
                </Text>
              ))
            : "-"}
        </View>
        <View style={{ flex: 1, paddingHorizontal: 6 }}>
          <Text style={styles.rowText}>{row?.fix?.name}</Text>
        </View>
        <View style={{ flex: 1, paddingHorizontal: 6 }}>
          {row?.fix?.isPartRequired ? (
            <>
              <JobCardSparePartToolTip
                brand={row.brand}
                isSparePartsServiceAvailable={isSparePartsServiceAvailable}
                partDetails={partDetails}
              />
              <Text style={styles.rowText}>
                <span style={{ fontFamily: FONT_FAMILY.three, fontSize: 13 }}>
                  HSN Code
                </span>
                {"\n"}
                {row?.hsn?.code || "---"}
              </Text>
            </>
          ) : (
            <Text style={[styles.rowText, { marginBottom: 3 }]}>N/A</Text>
          )}
        </View>
        <View style={{ flex: 0.7, paddingHorizontal: 6 }}>
          <PartDetailsToolTip
            partCount={row.partCount}
            partDetails={partDetails}
          />
        </View>
        <View style={{ flex: 0.7, paddingHorizontal: 6 }}>
          <Text style={styles.rowText}>
            {Number.isNaN(row?.partCost) ? 0 : row?.partCost.toFixed(2)}
          </Text>
        </View>
        <View style={{ flex: 0.7, paddingHorizontal: 6 }}>
          <Text style={styles.rowText}>
            {Number.isNaN(row?.labourCost) ? 0 : row?.labourCost.toFixed(2)}
          </Text>
          <Text
            style={[
              styles.rowText,
              { marginTop: 20, fontFamily: FONT_FAMILY.three, fontSize: 13 },
            ]}
          >
            Sac Code
          </Text>
          <Text style={styles.rowText}>{row?.sac?.code}</Text>
        </View>
        <View style={{ flex: 0.7, paddingHorizontal: 6 }}>
          <Text style={styles.rowText}>
            {Number.isNaN(totalCost) ? 0 : totalCost.toFixed(2)}
          </Text>
        </View>
        <View style={{ flex: 0.7, paddingHorizontal: 6 }}>
          <Text
            style={[
              styles.rowText,
              { fontFamily: FONT_FAMILY.three, color: statusColor },
            ]}
          >
            {status}
          </Text>
          {row?.timeline?.length ? (
            <TouchableOpacity
              style={{ flexDirection: "row", alignItems: "center" }}
              onPress={() => setShowTimeline(!showTimeline)}
            >
              <Text
                style={{
                  fontSize: 13,
                  fontFamily: FONT_FAMILY.three,
                  marginRight: 4,
                  color: THEME_COLORS.primaryPurple,
                }}
              >
                View Timeline
              </Text>
              <Icon
                name={showTimeline ? "chevron-small-up" : "chevron-small-down"}
                style={{ color: THEME_COLORS.primaryPurple, fontSize: 15 }}
              />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
      {showTimeline ? (
        <View
          style={{
            flexDirection: "row",
            backgroundColor: "white",
            paddingVertical: 25,
            paddingHorizontal: 25,
          }}
        >
          <MjrTimelineComponent data={timelineData} />
        </View>
      ) : null}
    </View>
  );
});

const JobCardView = (props: IJobCardViewProps) => {
  const { jobCardId } = props;
  const [worshopBayWiseRows, setWorkshopBayWiseRows] = useState([]);
  const [selectedWorkshopIndex, setSelectedWorkshopIndex] = useState(0);

  const { data, loading, error } = useQuery(GET_JOB_CARD_DATA, {
    variables: { refurbCarId: jobCardId.toString() },
    client: clientExternalV2,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const mappedData = data?.getJobCardCityTeamV2?.map((item) => {
        let rows = [];
        if (item?.refurbCar?.isBayLayoutEnabled)
          rows = JSON.parse(JSON.stringify(item?.jobCard?.microJobRows || []));
        else {
          item?.jobCard?.microJobRows?.map((mjr) => {
            if (mjr?.jobRow) {
              if (mjr.jobRow?.isAcceptedByWorkshop) rows.push(mjr);
            } else rows.push(mjr);
          });
        }
        return {
          rows,
          workshopBayType: item?.workshopBayType,
        };
      });
      setSelectedWorkshopIndex(0);
      setWorkshopBayWiseRows(mappedData || []);
    },
  });

  const headerComponent = useMemo(
    () => (
      <View style={{ backgroundColor: "white" }}>
        <View
          style={{
            flexDirection: "row",
            paddingVertical: 10,
            paddingHorizontal: 5,
            borderRadius: 8,
            backgroundColor: "#F8F7FF",
          }}
        >
          {header.map((item, i) => (
            <View
              key={i}
              style={{
                flex: header.slice(4).includes(item) ? 0.7 : 1,
                paddingHorizontal: 6,
              }}
            >
              <Text style={{ fontSize: 15, fontFamily: FONT_FAMILY.three }}>
                {item}
              </Text>
            </View>
          ))}
        </View>
        <View style={{ height: 10, width: "100%", backgroundColor: "white" }} />
      </View>
    ),
    []
  );

  if (loading || error)
    return (
      <Text style={{ fontFamily: FONT_FAMILY.two }}>
        {error?.message || "Loading..."}
      </Text>
    );

  return (
    <View style={{ flex: 1 }}>
      <View
        style={{
          paddingBottom: 10,
          borderRadius: 5,
          backgroundColor: "white",
          flex: 1,
        }}
      >
        {data?.getJobCardCityTeamV2?.[0]?.refurbCar?.isBayLayoutEnabled ? (
          <FlatList
            horizontal={true}
            showsHorizontalScrollIndicator={false}
            data={data?.getJobCardCityTeamV2 || []}
            extraData={selectedWorkshopIndex}
            style={{
              flexGrow: 0,
              marginBottom: 15,
              overflow: "visible",
              paddingTop: 30,
              top: -15,
            }}
            renderItem={({ item, index }) => (
              <View style={{ overflow: "visible" }}>
                {index == selectedWorkshopIndex ? (
                  <View
                    style={{
                      zIndex: 10,
                      top: -25,
                      position: "absolute",
                      backgroundColor: "rgba(28, 202, 119, 0.2)",
                      paddingVertical: 2,
                      paddingHorizontal: 8,
                      borderRadius: 5,
                    }}
                  >
                    <Text
                      style={[
                        styles.rowText,
                        { fontSize: 12, marginTop: 0.5, color: "black" },
                      ]}
                    >
                      {item?.workCompletedAt
                        ? `Completed On ${moment(item?.workCompletedAt).format("D/M/YY h:mm a")}`
                        : "WIP"}
                    </Text>
                  </View>
                ) : null}
                <TouchableOpacity
                  style={{ marginRight: 50 }}
                  onPress={() => setSelectedWorkshopIndex(index)}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      fontFamily: FONT_FAMILY.three,
                      color:
                        index == selectedWorkshopIndex
                          ? THEME_COLORS.primaryPurple
                          : "#979797",
                    }}
                  >
                    {item?.workshopBayType?.name}
                  </Text>
                  <Text
                    style={[
                      styles.rowText,
                      { fontSize: 12, marginTop: 0.5, color: "black" },
                    ]}
                  >{`Technician:  ${item?.assignedTechnicianNameAndTime?.[0]?.label}`}</Text>
                  <Text
                    style={[
                      styles.rowText,
                      { fontSize: 12, marginTop: 0.5, color: "black" },
                    ]}
                  >{`Assigned On:  ${moment(item?.assignedTechnicianNameAndTime?.[1]?.label).format("Do MMM•h:mm a")}`}</Text>
                  <View
                    style={{
                      height: 2,
                      borderRadius: 2,
                      width: 33,
                      marginTop: 5,
                      backgroundColor:
                        index == selectedWorkshopIndex
                          ? THEME_COLORS.primaryPurple
                          : "white",
                    }}
                  />
                </TouchableOpacity>
              </View>
            )}
            keyExtractor={(_, index) => index.toString()}
          />
        ) : null}
        <View style={{ flex: 1 }}>
          <FlatList
            stickyHeaderIndices={[0]}
            ListHeaderComponent={headerComponent}
            data={worshopBayWiseRows?.[selectedWorkshopIndex]?.rows || []}
            keyExtractor={(_, index) => _.id?.toString()}
            renderItem={({ item, index }: any) => (
              <RowComponent
                row={item}
                isSparePartsServiceAvailable={
                  data?.getJobCardCityTeamV2?.[0]?.refurbCar?.isBayLayoutEnabled
                }
              />
            )}
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  rowText: {
    fontFamily: FONT_FAMILY.two,
    fontSize: 15,
    letterSpacing: 0.1,
  },
});

export default memo(JobCardView);
